import { useMemo, useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { listLeads, type LeadListParams, type LeadRow } from "../api/leads";
import { fmtDateTimeInZone, localDateInZone } from "../lib/time";

// Captured Leads (F6): what the reps learned on their calls, for the
// supervisor. Names and numbers arrive masked when the account's PII rule
// says so (the API decides; `masked` on the response is the only signal this
// page needs), so the same screen serves the floor supervisor and an admin.
//
// Date range and text search are server-side. The campaign and agent
// drop-downs filter the loaded page client-side, from the rows themselves:
// the floor supervisor's login cannot list campaigns or agents (both 403 for
// a restricted role), and the rows already carry both names.

const PER_PAGE = 200;

function csvEscape(v: unknown): string {
  const s = v === null || v === undefined ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(filename: string, header: string[], rows: unknown[][]) {
  const body = [header, ...rows].map((r) => r.map(csvEscape).join(",")).join("\n");
  const blob = new Blob([body], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function formatValue(v: LeadRow["details"][number]["value"]): string {
  if (v === null || v === undefined || v === "") return "";
  if (v === true) return "Yes";
  if (v === false) return "No";
  return String(v);
}

function fullName(r: LeadRow): string {
  return [r.first_name, r.last_name].filter(Boolean).join(" ");
}

export function LeadsPage() {
  const [range, setRange] = useState<LeadListParams>({});
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [campaign, setCampaign] = useState<string>("");
  const [agent, setAgent] = useState<string>("");

  const params: LeadListParams = { ...range, q: search || undefined, page, per_page: PER_PAGE };
  const leads = useQuery({
    queryKey: ["leads", params],
    queryFn: () => listLeads(params),
    placeholderData: keepPreviousData,
  });

  const data = leads.data;
  const tz = data?.timezone;
  const shownFrom = range.date_from ?? data?.date_from ?? "";
  const shownTo = range.date_to ?? data?.date_to ?? "";

  const presets = useMemo(() => {
    if (!tz) return [];
    const today = localDateInZone(tz);
    return [
      { label: "Today", value: {} as LeadListParams },
      { label: "Yesterday", value: { date_from: localDateInZone(tz, 1), date_to: localDateInZone(tz, 1) } },
      { label: "Last 7 days", value: { date_from: localDateInZone(tz, 6), date_to: today } },
      { label: "Last 30 days", value: { date_from: localDateInZone(tz, 29), date_to: today } },
    ];
  }, [tz]);

  const items = data?.items ?? [];
  const campaigns = useMemo(
    () => [...new Set(items.map((r) => r.campaign_name).filter((n): n is string => !!n))].sort(),
    [items],
  );
  const agents = useMemo(
    () => [...new Set(items.map((r) => r.agent_name).filter((n): n is string => !!n))].sort(),
    [items],
  );
  const shown = items.filter(
    (r) => (!campaign || r.campaign_name === campaign) && (!agent || r.agent_name === agent),
  );

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.per_page)) : 1;

  function exportCsv() {
    if (!tz) return;
    const labels = [...new Set(shown.flatMap((r) => r.details.map((d) => d.label)))];
    downloadCsv(
      `captured-leads-${shownFrom || "today"}-${shownTo || "today"}.csv`,
      ["Captured", "Agent", "Campaign", "Number", "First name", "Last name", "Wrap-up", ...labels],
      shown.map((r) => [
        fmtDateTimeInZone(r.updated_at, tz),
        r.agent_name ?? "",
        r.campaign_name ?? "",
        r.phone,
        r.first_name ?? "",
        r.last_name ?? "",
        r.disposition ?? "",
        ...labels.map((l) => formatValue(r.details.find((d) => d.label === l)?.value ?? null)),
      ]),
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold">Captured Leads</h1>
          <p className="text-sm text-gray-500">
            What your agents learned on their calls — names and the answers to each campaign's questions.
            {tz ? (
              <>
                {" "}Times are <span className="font-medium">{tz}</span>.
              </>
            ) : null}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <label htmlFor="leads-from" className="text-gray-600">From</label>
          <input
            id="leads-from"
            type="date"
            className="border border-gray-300 rounded px-2 py-1 text-sm"
            value={shownFrom}
            max={shownTo || undefined}
            onChange={(e) => {
              setPage(1);
              setRange((r) => ({ ...r, date_from: e.target.value || undefined }));
            }}
          />
          <label htmlFor="leads-to" className="text-gray-600">To</label>
          <input
            id="leads-to"
            type="date"
            className="border border-gray-300 rounded px-2 py-1 text-sm"
            value={shownTo}
            min={shownFrom || undefined}
            onChange={(e) => {
              setPage(1);
              setRange((r) => ({ ...r, date_to: e.target.value || undefined }));
            }}
          />
          {presets.map((p) => (
            <button
              key={p.label}
              type="button"
              className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200"
              onClick={() => {
                setPage(1);
                setRange(p.value);
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {data?.masked && (
        <p className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Names and phone numbers are partly hidden on this account. The answers your agents recorded are shown in full,
          except fields the campaign marks as private.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-3 text-sm">
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            setSearch(q.trim());
          }}
        >
          <label htmlFor="leads-q" className="sr-only">Search name or number</label>
          <input
            id="leads-q"
            className="border border-gray-300 rounded px-3 py-1.5 text-sm w-56"
            placeholder="Search name or number"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button type="submit" className="px-3 py-1.5 rounded bg-gray-100 hover:bg-gray-200">Search</button>
        </form>
        <label htmlFor="leads-campaign" className="text-gray-600 ml-2">Campaign</label>
        <select
          id="leads-campaign"
          className="border border-gray-300 rounded px-2 py-1.5 text-sm"
          value={campaign}
          onChange={(e) => setCampaign(e.target.value)}
        >
          <option value="">All</option>
          {campaigns.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <label htmlFor="leads-agent" className="text-gray-600">Agent</label>
        <select
          id="leads-agent"
          className="border border-gray-300 rounded px-2 py-1.5 text-sm"
          value={agent}
          onChange={(e) => setAgent(e.target.value)}
        >
          <option value="">All</option>
          {agents.map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
        <span className="ml-auto text-gray-500">
          {data ? `${shown.length} of ${data.total} shown` : ""}
        </span>
        <button
          type="button"
          onClick={exportCsv}
          disabled={shown.length === 0}
          className="px-3 py-1.5 rounded bg-brand hover:bg-brand-dark text-white disabled:opacity-40"
        >
          Download CSV
        </button>
      </div>

      {leads.isError && <p className="text-red-600 text-sm mb-3">{(leads.error as Error).message}</p>}
      {leads.isLoading && !data && <p className="text-gray-500 text-sm">Loading…</p>}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500">
            <tr>
              <th className="px-4 py-2">Captured</th>
              <th className="px-4 py-2">Agent</th>
              <th className="px-4 py-2">Campaign</th>
              <th className="px-4 py-2">Number</th>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Details</th>
              <th className="px-4 py-2">Wrap-up</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data && shown.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-3 text-gray-500">
                  Nothing captured in this range yet. Agents fill in lead details from the call console while they talk.
                </td>
              </tr>
            ) : (
              shown.map((r) => (
                <tr key={r.lead_capture_id} className="align-top">
                  <td className="px-4 py-2 whitespace-nowrap text-gray-600">
                    {tz ? fmtDateTimeInZone(r.updated_at, tz) : ""}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">{r.agent_name ?? <span className="text-gray-400">—</span>}</td>
                  <td className="px-4 py-2">{r.campaign_name ?? <span className="text-gray-400">—</span>}</td>
                  <td className="px-4 py-2 font-mono whitespace-nowrap">{r.phone}</td>
                  <td className="px-4 py-2 font-medium whitespace-nowrap">
                    {fullName(r) || <span className="text-gray-400">no name</span>}
                  </td>
                  <td className="px-4 py-2">
                    {r.details.length === 0 ? (
                      <span className="text-gray-400">—</span>
                    ) : (
                      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
                        {r.details.map((d) => (
                          <div key={d.key} className="contents">
                            <dt className="text-gray-500 whitespace-nowrap">{d.label}</dt>
                            <dd className="min-w-0 break-words">{formatValue(d.value) || <span className="text-gray-300">—</span>}</dd>
                          </div>
                        ))}
                      </dl>
                    )}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {r.disposition ? (
                      <span className="px-2 py-0.5 rounded text-xs bg-gray-200 text-gray-700">{r.disposition}</span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {data && totalPages > 1 && (
        <div className="flex items-center justify-end gap-2 mt-3 text-sm">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-gray-500">Page {page} of {totalPages}</span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
